import React, { useEffect } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RoundButton } from '@/components/UI/RoundButton';
import { getWithdrawHistory, getWithdrawInfo } from '@/store/actions/paymentActions';
import { DATE_FORMAT_DD_MM_YYYY } from '@/helpers/common';
import { prettyFloatMoney } from '@/helpers/utils';
import { cancelWithdrawRequest } from '@/store/services/paymentServices';
import { prettyDate } from '@/helpers/date';
import './index.scss';

const REQUEST_STATUSES = t => ({
  new: { id: 'new', title: t('req_status.sent') },
  in_process: { id: 'in_process', title: t('req_status.in_process') },
  completed: { id: 'completed', title: t('req_status.processed') },
  cancelled: { id: 'cancelled', title: t('req_status.cancelled') },
  refund: { id: 'refund', title: t('req_status.refund') },
  rejected_from_lhv: { id: 'rejected_from_lhv', title: t('req_status.rejected_by_bank') },
});

const WithdrawHistory = ({ accountType, className }) => {
  const dispatch = useDispatch();
  const [params, setParams] = React.useState({
    page: 1,
    limit: 10,
    account_type: accountType,
  });
  const { t } = useTranslation('withdraw');
  const { withdrawHistory } = useSelector(state => state.paymentStore);

  useEffect(() => {
    dispatch(getWithdrawHistory(params));
  }, []);

  const getNext = totalPages => {
    if (params.page < totalPages) {
      const nextPage = params.page + 1;
      dispatch(
        getWithdrawHistory(
          {
            ...params,
            page: nextPage,
          },
          true,
        ),
      );

      return this.setState(prevState => ({ ...prevState, page: nextPage }));
    }
  };

  const { page } = params;
  const { data } = withdrawHistory;

  if (!data || !data?.total_count) {
    return null;
  }

  return (
    <div className={classnames('withdraw_history', className)}>
      <h2 className="f-26 f-500">{t('withdraw_history.withdrawals_history')}</h2>
      <ul className="withdraw_history__list">
        {data.list.map(request => (
          <RequestItem request={request} key={request.id} />
        ))}
      </ul>
      {data && page < data.total_pages && (
        <RoundButton
          type="button"
          label={t('withdraw_history.show_more')}
          className="withdraw_history__more"
          onClick={() => getNext(data.total_pages)}
        />
      )}
    </div>
  );
};

export default WithdrawHistory;

const RequestItem = ({ request }) => {
  const [status, setStatus] = React.useState(request.status);
  const dispatch = useDispatch();
  const { t } = useTranslation('withdraw');
  const REQUEST_STATUSES_T = REQUEST_STATUSES(t);

  const handleCancel = () => {
    cancelWithdrawRequest(request.id).then(res => {
      res && res.success && setStatus(REQUEST_STATUSES.cancelled.id);
      if (res && res.success) {
        dispatch(getWithdrawInfo({}, true));
      }
    });
  };

  return (
    <li className="withdraw_history__list_item">
      <Trans
        ns="withdraw"
        i18nKey="withdraw_history.transfer"
        values={{
          date: prettyDate(request.created_at, DATE_FORMAT_DD_MM_YYYY),
          amount: prettyFloatMoney(request.amount),
          accountType: request.external_account.account_type,
          accountNumber: request.external_account.account_number,
          statusTitle: REQUEST_STATUSES_T[status]?.title ?? status,
        }}
        components={{
          Span: <span style={{ textTransform: 'capitalize' }} />,
        }}
      />{' '}
      {status === REQUEST_STATUSES_T.new.id && (
        <button type="button" className="withdraw_history__cancel" onClick={handleCancel}>
          {t('withdraw_history.cancel')}
        </button>
      )}
    </li>
  );
};
